/**
 * Property 포매터 — 정책 강제 layer.
 *
 * 모든 가격/상태/콘텐츠 텍스트는 이 모듈을 거쳐서 UI에 노출된다.
 * 새 컴포넌트가 Property.min_price 등을 직접 표시하지 않도록 강제.
 */

import { stripEmoji } from '../policy/content-rules';

/** 가격 라벨 — min/max 정보 없을 때만 폴백. */
export const PRICE_INQUIRY_LABEL = '가격 문의' as const;

/**
 * 만원 단위 가격을 "X억 Y,YYY만원" 형식으로 포맷.
 *  41790 → "4억 1,790만원"
 *  10000 → "1억원"
 *  3500  → "3,500만원"
 */
function formatManwon(price: number): string {
  if (price >= 10000) {
    const eok = Math.floor(price / 10000);
    const rem = price % 10000;
    if (rem === 0) return `${eok}억원`;
    return `${eok}억 ${rem.toLocaleString()}만원`;
  }
  return `${price.toLocaleString()}만원`;
}

/**
 * 분양가 표시값.
 *
 * 정책 변경 (사용자 피드백 2026-05-21): 실제 가격을 우선 노출.
 *   - min·max 둘 다 → "X억 Y천만 ~ A억 B천만원"
 *   - min 만 → "X억 Y천만원부터"
 *   - max 만 → "최고 A억 B천만원"
 *   - 둘 다 없음 → "가격 문의" (폴백)
 *
 * min/max 동일 → 단일 가격 ("X억 Y천만원").
 */
export function formatPriceForDisplay(
  min?: number | null,
  max?: number | null,
): string {
  const m = typeof min === 'number' && min > 0 ? min : null;
  const M = typeof max === 'number' && max > 0 ? max : null;
  if (m && M) {
    if (m === M) return formatManwon(m);
    return `${formatManwon(m)} ~ ${formatManwon(M)}`;
  }
  if (m) return `${formatManwon(m)}부터`;
  if (M) return `최고 ${formatManwon(M)}`;
  return PRICE_INQUIRY_LABEL;
}

/** '분양예정' 배지 문구 (대표 지시 2026-09-21: 구 "사전예약" 배지를 "분양예정"으로 통일). */
export const UPCOMING_BADGE_LABEL = '분양예정';

/**
 * '분양예정' 배지 표시 여부 — status='분양예정' 또는 관심고객 사전등록(is_pre_registration) 현장.
 * 카드·목록·상세·지도 목록·필터가 모두 이 기준을 쓴다.
 */
export function isUpcomingBadge(status: string | null | undefined, isPreRegistration: boolean): boolean {
  return isPreRegistration || (status ?? '').trim() === '분양예정';
}

/** (2026-09-28) 즉시입주 잔여세대 현장을 '분양중'으로 보는 상태 */
export const REOPEN_SELLING = new Set(['재오픈예정', '분양중', '마감임박']);

/** 홈·목록 필터에서 '분양중'으로 셀지 — 지도(getDisplayStatus)와 같은 기준 */
export const isSellingNow = (p: { status?: string | null; is_reopen?: boolean | null }): boolean =>
  p.status === '분양중' || p.status === '마감임박' || (p.is_reopen === true && REOPEN_SELLING.has(p.status || ''));

/**
 * '즉시입주 잔여세대' 배지 (대표 결정 2026-09-28) — 입주 후 미분양으로 재오픈하는 현장(is_reopen).
 * 상태가 '재오픈예정'이면 그것도 함께 적는다.
 */
export const REOPEN_BADGE_LABEL = '즉시입주 잔여세대';
export function reopenBadgeLabel(status: string | null | undefined, isReopen: boolean): string | null {
  if (!isReopen) return null;
  return (status ?? '').trim() === '재오픈예정' ? `${REOPEN_BADGE_LABEL} · 재오픈예정` : REOPEN_BADGE_LABEL;
}

/**
 * 분양 상태 마스킹.
 *
 * 정책: 분양중·마감임박 등 일반 상태는 UI 노출 금지.
 * 단, '분양예정'(status=분양예정 또는 is_pre_registration)만 허용.
 *
 * @returns chip 텍스트 또는 null (표시 안 함)
 */
export function maskPropertyStatus(
  status: string | null | undefined,
  isPreRegistration: boolean,
  isReopen = false,
): string | null {
  const reopen = reopenBadgeLabel(status, isReopen);
  if (reopen) return reopen;
  if (isUpcomingBadge(status, isPreRegistration)) return UPCOMING_BADGE_LABEL;
  // 그 외 모든 status는 마스킹
  return null;
}

/**
 * 설명/소개 텍스트에서 이모지 제거.
 *
 * 정책: 정보형 UI(헤더·카드·표·CTA)에 이모지 금지.
 * description / detailed_description 등의 사용자 콘텐츠에 적용.
 */
export function sanitizeContentText(text: string | null | undefined): string {
  if (!text) return '';
  return stripEmoji(text).trim();
}

/**
 * 단지명 정리 — 동(洞) prefix가 이미 있으면 중복 제거.
 *
 * 예: "범어동 범어동 어나드 범어" → "범어동 어나드 범어"
 */
export function normalizePropertyTitle(title: string): string {
  if (!title) return '';
  // 동/구 단어가 연속으로 두 번 나오는 패턴 제거
  return title.replace(/^(\S+동)\s+\1/, '$1').trim();
}

/**
 * 세대수 표시 (2026-10-01 대표: 혼합 단지는 '아파트 N세대 · 오피스텔 M실').
 * 백엔드 Property.units_label() 과 같은 규칙. total_units 는 전체(아파트+오피스텔), officetel_units 는 그중 오피스텔.
 *   - 혼합: full '아파트 360세대 · 오피스텔 78실' / short '360세대 · 78실'
 *   - 오피스텔 단지(property_type '오피스텔' 또는 전부 오피스텔): '96실'
 *   - 그 외 '1,098세대', 세대수 없으면 ''
 */
export interface UnitsSource {
  total_units?: number | null;
  officetel_units?: number | null;
  property_type?: string | null;
}

export function formatUnits(p: UnitsSource | null | undefined, opts: { short?: boolean } = {}): string {
  const total = p && typeof p.total_units === 'number' && p.total_units > 0 ? p.total_units : 0;
  if (!total) return '';
  const off = typeof p!.officetel_units === 'number' && p!.officetel_units > 0 ? p!.officetel_units : 0;
  if (off && off < total) {
    const apt = (total - off).toLocaleString();
    return opts.short ? `${apt}세대 · ${off.toLocaleString()}실` : `아파트 ${apt}세대 · 오피스텔 ${off.toLocaleString()}실`;
  }
  if (p!.property_type === '오피스텔' || off === total) return `${total.toLocaleString()}실`;
  return `${total.toLocaleString()}세대`;
}
